import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { doctorName } from './scheduleUtils';

/**
 * DoctorFilter — the doctor show/hide toggles folded into one dropdown, for
 * views where the doctors are already the column headings (Day view), so the
 * toolbar stays one line and the schedule keeps the screen.
 */
const DoctorFilter = ({ doctors, hidden, colours, me, onToggle, onSolo, onShowAll }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const shown = doctors.filter((d) => !hidden.has(d.uid)).length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Choose which doctors to show"
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 hover:border-brand-navy"
      >
        <span className="flex -space-x-1">
          {doctors.slice(0, 6).map((d) => (
            <span key={d.uid} className="h-3 w-3 rounded-full ring-2 ring-white" style={{ background: hidden.has(d.uid) ? '#e2e8f0' : colours[d.uid] }} />
          ))}
        </span>
        Doctors
        <span className={shown < doctors.length ? 'font-semibold text-brand-mid' : 'text-slate-400'}>{shown}/{doctors.length}</span>
        <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
          {doctors.map((d) => (
            <div key={d.uid} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50">
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!hidden.has(d.uid)} onChange={() => onToggle(d.uid)} className="accent-brand-navy" />
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colours[d.uid] }} />
                <span className="truncate">{doctorName(d)}{d.uid === me && <span className="text-slate-400"> (you)</span>}</span>
              </label>
              <button
                type="button"
                onClick={() => { onSolo(d.uid); setOpen(false); }}
                className="text-[11px] font-semibold text-brand-mid opacity-0 hover:underline group-hover:opacity-100"
              >
                Only
              </button>
            </div>
          ))}
          <div className="mt-1 border-t border-slate-100 px-2 pt-1.5">
            <button type="button" onClick={onShowAll} disabled={shown === doctors.length} className="text-xs font-semibold text-brand-mid hover:underline disabled:text-slate-300 disabled:no-underline">
              Show all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorFilter;
